<template>
    <div class="form-group dual-select-container">
        <div class="d-flex justify-content-between mb-2">
            <label v-if="left_label" class="form-label">
                {{ translatedLeftLabel }}
            </label>
            <label v-if="right_label" class="form-label">
                {{ translatedRightLabel }}
            </label>
        </div>
        <div class="d-flex">
            <select
                multiple
                class="form-control h-100"
                :size="size"
                v-model="leftSelected"
                :disabled="isLoading"
            >
                <option
                    v-for="item in availableItems"
                    :key="item[trackBy]"
                    :value="item"
                >
                    {{ item[labelKey] }}
                </option>
            </select>

            <div class="d-flex flex-column justify-content-center mx-2">
                <button
                    type="button"
                    class="btn btn-outline-primary mb-2"
                    @click="moveRight"
                    :disabled="isLoading || leftSelected.length === 0"
                >
                    →
                </button>
                <button
                    type="button"
                    class="btn btn-outline-primary"
                    @click="moveLeft"
                    :disabled="isLoading || rightSelected.length === 0"
                >
                    ←
                </button>
            </div>

            <select
                multiple
                class="form-control h-100"
                :size="size"
                v-model="rightSelected"
                :disabled="isLoading"
            >
                <option
                    v-for="item in selectedItems"
                    :key="item[trackBy]"
                    :value="item"
                >
                    {{ item[labelKey] }}
                </option>
            </select>
        </div>

        <small v-if="isLoading" class="text-muted">
            {{ $t('users.users_msg__loading') }}
        </small>
        <small v-if="errorMessage" class="text-danger">
            {{ errorMessage }}
        </small>
    </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { getOptions } from '@/api/users';

export default {
    name: 'DualSelectInput',
    props: {
        field: {
            type: String,
            required: true,
        },
        // 左側標籤（可選項）
        left_label: {
            type: String,
            default: '',
        },
        // 右側標籤（已選項）
        right_label: {
            type: String,
            default: '',
        },
        // 初始值（已選取的 ID 陣列或物件陣列）- 支援 v-model
        modelValue: {
            type: Array,
            default: () => [],
        },
        // 固定選項列表
        options: {
            type: Array,
            default: () => [],
        },
        // 從 API 取得選項的 URL
        options_url: {
            type: String,
            default: '',
        },
        // 選項的顯示屬性名稱
        option_label: {
            type: String,
            default: 'name',
        },
        // 選項的追蹤屬性名稱（唯一識別）
        option_track_by: {
            type: String,
            default: 'id',
        },
        // 下拉選單顯示行數
        size: {
            type: [Number, String],
            default: 10,
        },
        // 是否必填
        required: {
            type: String,
            default: 'false',
        },
        // 依賴的欄位名稱（用於動態更新 options_url）
        depends_on: {
            type: String,
            default: '',
        },
        // 動態 URL 模板（包含 {value} 占位符）
        dynamic_url_template: {
            type: String,
            default: '',
        },
    },
    emits: ['update:modelValue', 'valid', 'options-loaded'],
    setup(props, { emit }) {
        const { t } = useI18n();
        const allItems = ref([]);
        const leftSelected = ref([]);
        const rightSelected = ref([]);
        const isLoading = ref(false);
        const errorMessage = ref('');
        const isOptionsLoaded = ref(false);

        const labelKey = computed(() => props.option_label || 'name');
        const trackBy = computed(() => props.option_track_by || 'id');

        const translatedLeftLabel = computed(() => {
            if (!props.left_label) return '';
            return t(`users.${props.left_label}`);
        });

        const translatedRightLabel = computed(() => {
            if (!props.right_label) return '';
            return t(`users.${props.right_label}`);
        });

        // 計算可選項（未選取的項目）
        const availableItems = computed(() => {
            return allItems.value.filter(item => !item._selected);
        });

        // 計算已選項
        const selectedItems = computed(() => {
            return allItems.value.filter(item => item._selected);
        });

        // 取得已選取項目的 ID 陣列
        const selectedIds = computed(() => {
            return selectedItems.value.map(item => item[trackBy.value]);
        });

        // 從 props.modelValue 取得初始選取的 ID 陣列
        const getInitialSelectedIds = () => {
            if (
                !Array.isArray(props.modelValue) ||
                props.modelValue.length === 0
            ) {
                return [];
            }
            // 判斷 modelValue 是 ID 陣列還是物件陣列
            if (typeof props.modelValue[0] === 'object') {
                return props.modelValue.map(item => item[trackBy.value]);
            }
            return props.modelValue;
        };

        // 從 API 載入選項
        const fetchOptions = async () => {
            if (props.options_url) {
                try {
                    isLoading.value = true;
                    errorMessage.value = '';
                    const response = await getOptions(props.options_url);
                    const fetchedOptions = response || [];

                    // 使用 props.value 來決定初始選取狀態
                    const initialSelectedIds = getInitialSelectedIds();
                    allItems.value = fetchedOptions.map(option => ({
                        ...option,
                        _selected: initialSelectedIds.includes(
                            option[trackBy.value]
                        ),
                    }));

                    isOptionsLoaded.value = true;
                    emit('options-loaded', {
                        field: props.field,
                        options: fetchedOptions,
                    });
                } catch (error) {
                    console.error('Error fetching options:', error);
                    errorMessage.value = t(
                        'users.users_msg__failed_to_load_options'
                    );
                    allItems.value = [];
                    isOptionsLoaded.value = true;
                } finally {
                    isLoading.value = false;
                }
            } else if (
                Array.isArray(props.options) &&
                props.options.length > 0
            ) {
                // 使用固定選項
                const initialSelectedIds = getInitialSelectedIds();
                allItems.value = props.options.map(option => {
                    const normalizedOption =
                        typeof option === 'string'
                            ? {
                                  [labelKey.value]: option,
                                  [trackBy.value]: option,
                              }
                            : option;
                    return {
                        ...normalizedOption,
                        _selected: initialSelectedIds.includes(
                            normalizedOption[trackBy.value]
                        ),
                    };
                });
                isOptionsLoaded.value = true;
            } else {
                allItems.value = [];
                isOptionsLoaded.value = true;
            }
        };

        // 初始化已選取項目
        const initializeSelectedItems = () => {
            if (
                !Array.isArray(props.modelValue) ||
                props.modelValue.length === 0
            ) {
                // 清空選取狀態
                allItems.value.forEach(item => {
                    item._selected = false;
                });
                return;
            }

            // 判斷 modelValue 是 ID 陣列還是物件陣列
            const isObjectArray =
                props.modelValue.length > 0 &&
                typeof props.modelValue[0] === 'object';

            if (isObjectArray) {
                // 物件陣列：比對 trackBy 屬性
                const selectedIds = props.modelValue.map(
                    item => item[trackBy.value]
                );
                allItems.value.forEach(item => {
                    item._selected = selectedIds.includes(item[trackBy.value]);
                });
            } else {
                // ID 陣列：直接比對
                allItems.value.forEach(item => {
                    item._selected = props.modelValue.includes(
                        item[trackBy.value]
                    );
                });
            }
        };

        // 移動到右側（選取）
        const moveRight = () => {
            leftSelected.value.forEach(item => {
                const found = allItems.value.find(
                    i => i[trackBy.value] === item[trackBy.value]
                );
                if (found) {
                    found._selected = true;
                }
            });
            leftSelected.value = [];
            emitValue();
        };

        // 移動到左側（取消選取）
        const moveLeft = () => {
            rightSelected.value.forEach(item => {
                const found = allItems.value.find(
                    i => i[trackBy.value] === item[trackBy.value]
                );
                if (found) {
                    found._selected = false;
                }
            });
            rightSelected.value = [];
            emitValue();
        };

        // 發送更新事件
        const emitValue = () => {
            const ids = selectedIds.value;
            emit('update:modelValue', ids);
            validateValue(ids);
        };

        // 驗證值
        const validateValue = ids => {
            if (props.required === 'true' && ids.length === 0) {
                errorMessage.value = t(
                    'users.users_msg__this_field_is_required'
                );
                emit('valid', false);
            } else {
                errorMessage.value = '';
                emit('valid', true);
            }
        };

        // 公開的驗證方法
        const checkValue = () => {
            validateValue(selectedIds.value);
        };

        // 公開的重置方法
        const reset = () => {
            leftSelected.value = [];
            rightSelected.value = [];
            allItems.value.forEach(item => {
                item._selected = false;
            });
            emit('update:modelValue', []);
        };

        // 監聯 options_url 變化
        watch(
            () => props.options_url,
            newUrl => {
                // 清空選取狀態和重置載入狀態
                leftSelected.value = [];
                rightSelected.value = [];
                isOptionsLoaded.value = false;

                // 如果 URL 變更為空的查詢（search=0），清空所有選項
                if (newUrl && newUrl.includes('search=0')) {
                    allItems.value = [];
                    isOptionsLoaded.value = true;
                    emit('update:modelValue', []);
                } else if (newUrl) {
                    fetchOptions();
                } else {
                    // 沒有 URL，清空選項
                    allItems.value = [];
                    isOptionsLoaded.value = true;
                }
            },
            { immediate: true }
        );

        // 監聽固定 options 變化
        watch(
            () => props.options,
            newOptions => {
                if (Array.isArray(newOptions) && !props.options_url) {
                    fetchOptions();
                }
            },
            { immediate: true, deep: true }
        );

        // 監聽 modelValue 變化，同步選取狀態
        watch(
            () => props.modelValue,
            () => {
                if (isOptionsLoaded.value) {
                    initializeSelectedItems();
                }
            },
            { deep: true }
        );

        // 當選項載入完成後，初始化選取狀態
        watch(
            () => isOptionsLoaded.value,
            loaded => {
                if (loaded) {
                    initializeSelectedItems();
                    emitValue();
                }
            }
        );

        onMounted(() => {
            if (!props.options_url && props.options.length === 0) {
                isOptionsLoaded.value = true;
            }
        });

        return {
            allItems,
            leftSelected,
            rightSelected,
            availableItems,
            selectedItems,
            selectedIds,
            isLoading,
            errorMessage,
            labelKey,
            trackBy,
            translatedLeftLabel,
            translatedRightLabel,
            moveRight,
            moveLeft,
            checkValue,
            fetchOptions,
            reset,
        };
    },
};
</script>

<style scoped>
.dual-select-container {
    margin-bottom: 1rem;
}

.dual-select-container .d-flex {
    display: flex;
    gap: 0;
}

.dual-select-container select {
    flex: 1;
    min-height: 200px;
}

.dual-select-container .btn {
    min-width: 40px;
}

.text-danger {
    color: #e3342f;
}

.text-muted {
    color: var(--ca-color-text-muted, #6c757d);
}
</style>
